import * as React from 'react';
import { Search } from 'rcd-cpp-ui/ui/icon/Search';
import { Arrow } from 'rcd-cpp-ui/ui/icon/Arrow';
import { DataType } from '../../enums/DataType';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { View } from '../../types/View';
import { cn, parseField } from '../../utils';
import './filters.scss';
import { TextFilter } from './TextFilter/textFilter';
import { DateFilter } from './DateFilter/dateFilter';
import { Dropbox } from '../Dropbox/dropbox';
import { NumberFilter } from './NumberFilter/numberFilter';
import { AddFilters } from './AddFilters/addFilters';
import {
  ReferenceFilter,
  SingleValueReferenceFilter
} from './ReferenceFilter/referenceFilter';
import { EnumFilter } from './EnumFilter/enumFilter';
import { PrimaryRelationTypeFilter } from './PrimaryFilter/primaryFilter';
import { FieldType } from '../../enums/FieldType';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { BooleanFilter } from './BooleanFilter/booleanFilter';
import { ComputedFilter } from './ComputedFilter/computedFilter';
import { AuditFilter } from './AuditFilter/auditFilter';
import { Op } from '../../enums/Op';
import { AppliedFilters } from './AppliedFilters/appliedFilters';
import { SeeMoreFilters } from '../Filters/SeeMoreFilters/seeMoreFilters';
import { NodeType } from '../../enums/NodeType';
import { useSelector } from '../../utils/useSelector';
import { ExpiryDateFilter } from './ExpiryDateFilter/expiryDateFilter';
import { EmployeeRelationFilter } from './EmployeeRelationFilter/employeeRelationFilter';
import { FieldKey } from '../../enums/FieldKey';
import { FiltersProps } from '../../types/FilterProps';
import { SeriesFilter } from './SeriesFilter/seriesFilter';

export const Filters = (props: FiltersProps) => {
  const {
    filters,
    defaultFilters,
    preappliedFilters,
    schemas,
    getAPI,
    location,
    setFilters,
    seeAllFilters,
    viewRefId
  } = props;
  const [lastAdded, setLastAdded] = React.useState<{
    field: string;
    uuid?: string;
  }>(null);
  const [cache] = React.useState({});
  const userSettings = useSelector(s => s.planning?.userSettings || {});
  const selected = useSelector(s => s.view?.selected);
  const viewFilters = useSelector(s => s.view?.selected?.props?.filters);
  const refId = selected?.props?.refId;
  const userSetting = userSettings[refId];
  const seeMoreFilters = userSetting?.props?.seeMoreFilters;
  const isMyExpiryDashBoardModal = useSelector(
    s => s.viewModal?.isMyExpiryDashboard
  );
  const setFilter = !props.setFilter
    ? null
    : React.useCallback(
        (filter: View.FiltersField, reload?: boolean) => {
          setLastAdded(null);
          props.setFilter(filter, reload);
        },
        [props.setFilter]
      );
  const removeFilter = !props.removeFilter
    ? null
    : React.useCallback(
        (filter: View.FiltersField) => {
          setLastAdded(null);
          props.removeFilter(filter);
        },
        [props.removeFilter]
      );
  const addFilter = !props.addFilter
    ? null
    : React.useCallback(
        (filter: View.FiltersField) => {
          if (props.setFilter) setLastAdded(filter);
          props.addFilter(filter);
        },
        [props.addFilter, props.setFilter]
      );

  const addAnotherFilter = !props.addAnotherFilter
    ? null
    : React.useCallback(
        (filter: View.FiltersField) => {
          if (props.setFilter) setLastAdded(filter);
          props.addAnotherFilter(filter);
        },
        [props.addAnotherFilter, props.setFilter]
      );

  if (!schemas) return null;

  const requiredFilters = filters?.filter(filter => filter.isRequired) || [];
  const nonRequiredFilters =
    filters?.filter(filter => !filter.isRequired) || [];
  const sortedFilters = [...requiredFilters, ...nonRequiredFilters];
  return (
    <>
      <div className="filters">
        {filters?.length > 4 && !seeAllFilters && <SeeMoreFilters />}
        {sortedFilters?.map((filter, index) => {
          const parsedFilter = parseField(filter.field, schemas);
          if (!parsedFilter) {
            return null;
          }
          const { schema, schemaField, schemaType } = parsedFilter;
          if (
            (!schema || !schemaField) &&
            schemaType != NodeType.SchemalessFilter
          )
            return null;

          if (seeMoreFilters || seeAllFilters || index < 4) {
            return (
              <Filter
                key={`${filter.uuid}`}
                {...props}
                addFilter={addFilter}
                addAnotherFilter={addAnotherFilter}
                setFilter={
                  location != 'view' && filter.isFromParent ? null : setFilter
                }
                removeFilter={
                  location != 'view' && filter.isFromParent
                    ? null
                    : location == 'view' && filter.isRequired
                      ? null
                      : removeFilter
                }
                filter={filter}
                schema={schema}
                cache={cache}
                field={schemaField}
                defaultShow={
                  !!lastAdded &&
                  ((!lastAdded?.uuid && lastAdded?.field == filter.field) ||
                    (!!lastAdded?.uuid && lastAdded?.uuid == filter.uuid))
                }
                view={location == 'view' ? selected : null}
                isMyExpiryDashBoardModal={isMyExpiryDashBoardModal}
              />
            );
          }
        })}
      </div>
      <div className="filter-links">
        {!!addFilter && (
          <div>
            <AddFilters
              addFilter={addFilter}
              addAnotherFilter={addAnotherFilter}
              filters={filters}
              schemas={schemas}
              defaultFilters={defaultFilters}
            />
          </div>
        )}
        {!!setFilters && (
          <>
            <div
              className="clear-filters"
              onClick={() => {
                location == 'view'
                  ? setFilters(
                      viewFilters?.map(f => {
                        return { ...f, uuid: Math.random().toString(10) };
                      }) || []
                    )
                  : setFilters(filters || [], true);
              }}
            >
              {location == 'view' ? `Reset My Filters` : `Clear My Filters`}
            </div>
            <AppliedFilters
              setFilter={setFilter}
              viewRefId={viewRefId}
              filters={filters}
              schemas={schemas}
              getAPI={getAPI}
              preappliedFilters={preappliedFilters}
            />
            {props.children}
          </>
        )}
      </div>
    </>
  );
};

const FilterComponents = {
  ['expiryDate']: ExpiryDateFilter,
  [FieldKey.ExcludeSecondary]: PrimaryRelationTypeFilter,
  [FieldKey.ItemEmployeeRelationType]: EmployeeRelationFilter,
  [FieldType.reference]: ReferenceFilter,
  [FieldType.computed]: ComputedFilter,
  [DataType.enum]: EnumFilter,
  [DataType.number]: NumberFilter,
  [DataType.string]: TextFilter,
  [DataType.longString]: TextFilter,
  [DataType.boolean]: BooleanFilter,
  [DataType.dateTime]: DateFilter,
  [DataType.date]: DateFilter,
  ['Item.series']: SeriesFilter
};

export interface FilterProps extends FiltersProps {
  filter: View.FiltersField;
  schema: Schema;
  field: SchemaField;
  defaultShow?: boolean;
  cache?: { [s: string]: Metadata };
  opts?: Op[];
  isMyExpiryDashBoardModal?: boolean;
  view?: View.Setting;
}

const hasFilter = ({ conditions }: View.FiltersField) => {
  if (conditions) {
    for (const c of conditions) {
      for (const [op, val] of Object.entries(c)) {
        if (typeof val == 'boolean') return true;
        if (Array.isArray(val) && val.length) return true;
        if (!Array.isArray(val) && val) return true;
      }
    }
  }
  return false;
};

const getFilterComponent = (
  f: SchemaField,
  v: View.Setting,
  isMyExpiryDashBoardModal?: boolean
) => {
  if (!f) return null;
  const { key, fieldType, dataType, relationType } = f;
  if (['MODIFIED_BY', 'CREATED_BY'].includes(relationType)) return AuditFilter;
  if (f.field == 'expiryDate' && isMyExpiryDashBoardModal)
    return FilterComponents[f.field];
  if (dataType == DataType.link && fieldType != FieldType.computed)
    return FilterComponents[DataType.string];
  if (dataType == DataType.date) return FilterComponents[dataType];
  if (dataType == DataType.number) return FilterComponents[dataType];
  if (key == 'Item.role' && v?.props?.isEFSBuilder)
    return SingleValueReferenceFilter;

  return (
    FilterComponents[key] ||
    FilterComponents[fieldType] ||
    FilterComponents[dataType]
  );
};

const Filter = (props: FilterProps) => {
  const {
    filter,
    field,
    setFilter,
    removeFilter,
    defaultShow,
    location,
    view,
    isMyExpiryDashBoardModal
  } = props;
  const F = getFilterComponent(field, view, isMyExpiryDashBoardModal);
  const onClick = React.useCallback(() => removeFilter(filter), [filter]);
  if (!F) return null;
  return (
    <div className={cn({ 'has-filter': hasFilter(filter) }, 'filter')}>
      <Dropbox
        className="filter-dropbox"
        defaultShow={
          defaultShow && (location == 'view' || !filter.isFromParent)
        }
        disabled={!setFilter}
        renderBox={() => <F {...props} />}
      >
        <Search />
        <span className={cn({ 'filter-required': filter.isRequired })}>
          {field.displayName}
        </span>
        {!!setFilter && <Arrow direction="down" />}
      </Dropbox>
      {!!removeFilter && (
        <Icon onClick={onClick}>
          <Close />
        </Icon>
      )}
    </div>
  );
};
